import { NameValuePair } from "../../schema/types/base";
import { ArrayString } from "../../schema/types/org/input";
import { Sidecar } from "../../schema/types/sidecar/entity";
import { SidecarInput } from "../../schema/types/sidecar/input";
import { Workload } from "../../schema/types/workload/entity";
import jsYaml from "js-yaml";
import { v4 as uuidv4 } from "uuid";
import { parseLink } from "../../utils/linkParser/linkParser";
import { handleDetermineNanosInDuration } from "./envoy/jwt/duration";
import { sentryLog } from "../../tools/sentry";
import { KindSelectState } from "../../components/antd/kindselect/KindSelect";

export function validateProviderName(name: string | undefined) {
  if (!name) return "";

  let restrictedName: string;
  if (name?.startsWith("cpln_")) {
    restrictedName = name.substring("cpln_".length);
  } else {
    restrictedName = `cpln_${name}`;
  }

  return restrictedName;
}

function extractDomain(url: string): string | null {
  const protocolRegex = /^(?:https?:\/\/)?/;
  if (!protocolRegex.test(url)) {
    return null;
  }

  const domainRegex = /^(?:https?:\/\/)?([^\/]+)/;
  const matches = url.match(domainRegex);
  if (matches && matches.length >= 2) {
    return matches[1];
  } else {
    return null;
  }
}

const defaultClusterType = "STRICT_DNS";
const defaultTransportSocket = "envoy.transport_sockets.tls";
const defaultHttpName = "envoy.filters.http.jwt_authn";
const defaultTypedConfigType = "type.googleapis.com/envoy.extensions.filters.http.jwt_authn.v3.JwtAuthentication";
const defaultPortValue = 443;
const defaultPriority = 50;

export interface cplnJwtInput {
  _cluster_type: string;
  _transport_socket: string;
  _http_name: string;
  _typed_config_type: string;
  _port_value: number;
  _priority: number;
  name: string;
  issuer: string;
  remote_jwks_http_uri: string;
  remote_jwks_http_uri_timeout: string;
  excludedWorkloads: KindSelectState[];
  audiences: ArrayString[];
  claim_to_headers: NameValuePair[];
  rules: {
    id: string;
    chosen: boolean;
    prefix: string;
    excludeAuth: boolean;
    headers: NameValuePair[];
  }[];
}

export function getDefaultCplnJwtInput(): cplnJwtInput {
  return {
    _cluster_type: defaultClusterType,
    _transport_socket: defaultTransportSocket,
    _http_name: defaultHttpName,
    _typed_config_type: defaultTypedConfigType,
    _port_value: defaultPortValue,
    _priority: defaultPriority,
    audiences: [],
    claim_to_headers: [],
    excludedWorkloads: [],
    issuer: "",
    name: "",
    remote_jwks_http_uri: "",
    remote_jwks_http_uri_timeout: "",
    rules: [],
  };
}

export function getDefaultSidecarInput(): SidecarInput {
  return {
    envoy: {
      type: "none",
      custom: "",
      jwt: getDefaultCplnJwtInput(),
    },
  };
}

export function inputToCplnJwt(input: cplnJwtInput) {
  return {
    clusters: [
      {
        name: validateProviderName(input.name),
        type: input._cluster_type,
        load_assignment: {
          cluster_name: validateProviderName(input.name),
          endpoints: [
            {
              lb_endpoints: [
                {
                  endpoint: {
                    address: {
                      socket_address: {
                        address: extractDomain(input.issuer),
                        port_value: input._port_value,
                      },
                    },
                  },
                },
              ],
            },
          ],
        },
        transport_socket: {
          name: input._transport_socket,
          typed_config: {
            "@type": "type.googleapis.com/envoy.extensions.transport_sockets.tls.v3.UpstreamTlsContext",
            sni: extractDomain(input.remote_jwks_http_uri),
          },
        },
      },
    ],
    http: [
      {
        name: input._http_name,
        priority: input._priority,
        ...(input.excludedWorkloads.length > 0
          ? {
              excludedWorkloads: input.excludedWorkloads,
            }
          : {}),
        typed_config: {
          "@type": input._typed_config_type,
          providers: {
            [validateProviderName(input.name)]: {
              audiences: input.audiences.map((i) => i.value),
              claim_to_headers: input.claim_to_headers
                .filter((i) => i.name !== "" && i.value !== "")
                .map((i) => ({
                  header_name: i.name,
                  claim_name: i.value,
                })),
              issuer: input.issuer,
              remote_jwks: {
                cache_duration: `${input.remote_jwks_http_uri_timeout}s`,
                http_uri: {
                  cluster: validateProviderName(input.name),
                  timeout: `${input.remote_jwks_http_uri_timeout}s`,
                  uri: input.remote_jwks_http_uri,
                },
              },
            },
          },
          rules: input.rules.map((rule) => {
            return {
              match: {
                prefix: rule.prefix,
                headers: rule.headers.map((h) => ({
                  name: h.name,
                  string_match: {
                    exact: h.value,
                  },
                })),
              },
              ...(rule.excludeAuth
                ? {}
                : {
                    requires: {
                      provider_name: validateProviderName(input.name),
                    },
                  }),
            };
          }),
        },
      },
    ],
  };
}

function getCplnHttpAndClusterOfSidecarEnvoy(https: any, clusters: any): { http: any; cluster: any } {
  const prefixedNames: { index: number; name: string }[] = [];

  if (https && clusters) {
    for (const httpIndexStr in https) {
      const httpIndex = Number(httpIndexStr);
      const httpObject = https[httpIndex];
      const httpProvider = Object.keys(httpObject.typed_config?.providers)[0];
      if (httpProvider.startsWith("cpln_")) {
        prefixedNames.push({ index: httpIndex, name: httpProvider });
      }
    }

    for (const prefixedName of prefixedNames) {
      const foundClusterIndex = clusters.findIndex((clusterObj) => clusterObj.name === prefixedName.name);
      if (foundClusterIndex > -1) {
        return {
          http: https[prefixedName.index],
          cluster: clusters[foundClusterIndex],
        };
      }
    }
  }

  return {
    http: undefined,
    cluster: undefined,
  };
}

export function sidecarToInput(sidecar?: Sidecar): SidecarInput {
  if (!sidecar?.envoy) {
    return getDefaultSidecarInput();
  }

  const { http, cluster } = getCplnHttpAndClusterOfSidecarEnvoy(sidecar.envoy?.http, sidecar.envoy?.clusters);

  const usesJwt = !!cluster && !!http;

  if (!usesJwt) {
    return {
      envoy: {
        type: "custom",
        custom: jsYaml.dump(sidecar),
        jwt: getDefaultCplnJwtInput(),
      },
    };
  }

  let rules: {
    id: string;
    chosen: boolean;
    prefix: string;
    excludeAuth: boolean;
    headers: NameValuePair[];
  }[] = [
    { id: uuidv4(), chosen: false, prefix: "/metric", excludeAuth: true, headers: [] },
    { id: uuidv4(), chosen: false, prefix: "/", excludeAuth: false, headers: [] },
  ];

  if (!!cluster && !!http && !!http?.typed_config?.rules && Array.isArray(http.typed_config.rules)) {
    rules = [];
    for (const ruleIndex in http.typed_config.rules) {
      const rule = http.typed_config.rules[ruleIndex];
      const ruleInput: {
        id: string;
        chosen: boolean;
        prefix: string;
        excludeAuth: boolean;
        headers: NameValuePair[];
      } = { id: uuidv4(), chosen: false, prefix: rule.match.prefix, excludeAuth: !rule.requires, headers: [] };
      if (!!rule.match.headers && Array.isArray(rule.match.headers)) {
        for (const header of rule.match.headers) {
          if (!header.name || !header.string_match || !header.string_match.exact) {
            continue;
          }
          ruleInput.headers.push({ id: uuidv4(), name: header.name, value: header.string_match.exact });
        }
      }
      rules.push(ruleInput);
    }
  }

  const res: SidecarInput = {
    envoy: {
      type: "jwt",
      custom: "",
      jwt: {
        _cluster_type: cluster?.type || defaultClusterType,
        _transport_socket: cluster?.transport_socket?.name || defaultTransportSocket,
        _http_name: http?.name || defaultHttpName,
        _typed_config_type: http?.typed_config?.["@type"] || defaultTypedConfigType,
        _port_value:
          cluster?.load_assignment?.endpoints?.[0]?.lb_endpoints?.endpoint?.address?.socket_address?.port_value ||
          defaultPortValue,
        _priority: http?.priority || defaultPriority,
        audiences: (http?.typed_config?.providers?.[cluster?.name]?.audiences || []).map((i: string) => ({
          id: uuidv4(),
          value: i,
        })),
        claim_to_headers: (http?.typed_config?.providers?.[cluster?.name]?.claim_to_headers || []).map(
          (i: { header_name: string; claim_name: string }) => ({
            id: uuidv4(),
            name: i.header_name,
            value: i.claim_name,
          }),
        ),
        excludedWorkloads: (http?.excludedWorkloads || [])
          .filter((i: string) => {
            // todo test if it is full link, if not, ask to fix it, should not accept just name
            const parsedWorkloadLink = parseLink(i, { useInputCtx: true });
            if (!parsedWorkloadLink.isValid) {
              return false;
            }
            return true;
          })
          .map((i: string) => {
            // todo test if it is full link, if not, ask to fix it, should not accept just name
            const { name, absolute } = parseLink(i, { useInputCtx: true });
            const state: KindSelectState = {
              value: name,
              link: absolute,
            };
            return state;
          }),
        issuer: http?.typed_config?.providers?.[cluster?.name]?.issuer || "",
        name: validateProviderName(cluster?.name),
        remote_jwks_http_uri: http?.typed_config?.providers?.[cluster?.name]?.remote_jwks?.http_uri?.uri || "",
        remote_jwks_http_uri_timeout: handleDetermineNanosInDuration(
          http?.typed_config?.providers?.[cluster?.name]?.remote_jwks?.http_uri?.timeout,
        )
          ? String(http?.typed_config?.providers?.[cluster?.name]?.remote_jwks?.http_uri?.timeout?.seconds)
          : http?.typed_config?.providers?.[cluster?.name]?.remote_jwks?.http_uri?.timeout?.slice(0, -1),
        rules: rules,
      },
    },
  };
  return res;
}

export function inputToSidecar(input: SidecarInput): Sidecar | undefined {
  if (input.envoy.type === "custom") {
    try {
      return {
        envoy: jsYaml.load(input.envoy.custom),
      };
    } catch (e) {
      sentryLog("failed to parse sidecar input to custom", {
        "data.input": input.envoy.custom,
        "data.type": input.envoy.type,
      });
    }
  } else if (input.envoy.type === "jwt") {
    return {
      envoy: inputToCplnJwt(input.envoy.jwt),
    };
  }

  return undefined;
}
